import Vue from "vue";
import { Job } from "@/models/job";
import { AuthService } from "@/services/auth-service";
import { InboxreaderService, ISkippedUploadFile } from "@/services/inboxreader-service";
import { JobService } from "@/services/job-service";
import { i18n } from "@/setup/i18n-setup";
import { EventEmitter } from "@/utils/event-emitter";

const POLL_INTERVAL = 5000;
const JOB_NAME_PREFIX = "inboxreader-upload-";

export type InboxUploadFileState = "done" | "busy" | "waiting";

export interface InboxUploadFile {
	name:string;
	state:InboxUploadFileState;
}

export class InboxUploadJob extends EventEmitter {
	public sendingFileNames:string[] = [];
	public uploadPercent:number = 0;
	private knownJobIds:string[] = [];
	private finishedJobIds:string[] = [];
	private namesByJob:{[id:string]:string[]} = {};
	private pollTimeout:ReturnType<typeof setTimeout> | null = null;

	constructor(){
		super();
		Vue.observable(JobService);
		JobService.on("job-stopped", this.onJobStopped);
		JobService.on("jobs-changed", this.syncJobs);
		this.syncJobs();
	}

	public get jobs():Job[] {
		return JobService.getJobs().filter(j=>j.Name.startsWith(JOB_NAME_PREFIX) && !this.finishedJobIds.includes(j.getID()));
	}

	public get isSending():boolean {
		return this.sendingFileNames.length > 0;
	}

	public get uploading():boolean {
		return this.isSending || this.jobs.length > 0;
	}

	public get files():InboxUploadFile[] {
		let result:InboxUploadFile[] = [];
		for (let job of this.jobs) {
			let names = job.StatusMessageData || [];
			let done = job.Progress < 0 ? 0 : Math.round(job.Progress * names.length);
			let started = job.isRunning || job.Progress >= 0;
			names.forEach((name, i)=>{
				let state:InboxUploadFileState = "waiting";
				if (i < done) {
					state = "done";
				} else if (i == done && started) {
					state = "busy";
				}
				result.push({name, state});
			});
		}
		for (let name of this.sendingFileNames) {
			result.push({name, state: "waiting"});
		}
		return result;
	}

	public get fileNames():string[] {
		return this.files.map(f=>f.name);
	}

	public get processedCount():number {
		return this.files.filter(f=>f.state == "done").length;
	}

	public get progress():number {
		let total = this.files.length;
		if (!total) return 0;
		return (this.processedCount / total) * 100;
	}

	public fileState(index:number):InboxUploadFileState {
		let file = this.files[index];
		return file ? file.state : "waiting";
	}

	public async start(files:File[], onDone?:(error:string)=>void):Promise<void> {
		this.sendingFileNames = files.map(f=>f.name);
		this.uploadPercent = 0;
		let result;
		try {
			result = await InboxreaderService.uploadFiles(files, percent=>{
				this.uploadPercent = percent;
			});
		} finally {
			this.sendingFileNames = [];
			this.uploadPercent = 0;
		}
		if (onDone) {
			this.once("done", onDone);
		}
		JobService.registerQueuedJob(result.job);
		this.notifySkipped(result.skippedFiles);
		this.syncJobs();
	}

	private syncJobs = ()=>{
		let jobs = this.jobs;
		let ids = jobs.map(j=>j.getID());
		let finished = this.knownJobIds.filter(id=>!ids.includes(id));
		this.knownJobIds = ids;
		for (let job of jobs) {
			this.namesByJob[job.getID()] = job.StatusMessageData || [];
		}
		if (finished.length) {
			let names = finished.map(id=>this.namesByJob[id] || []).reduce((a, b)=>a.concat(b), []);
			finished.forEach(id=>this.markFinished(id));
			this.notifyResult("", names);
			this.emit("done", "");
		}
		if (ids.length) {
			this.ensurePolling();
		}
	};

	private markFinished(id:string){
		delete this.namesByJob[id];
		this.knownJobIds = this.knownJobIds.filter(known=>known != id);
		if (this.finishedJobIds.includes(id)) return;
		this.finishedJobIds = [...this.finishedJobIds.slice(-49), id];
	}

	private onJobStopped = (job:Job)=>{
		if (!job.Name.startsWith(JOB_NAME_PREFIX)) return;
		let id = job.getID();
		if (this.finishedJobIds.includes(id)) return;
		this.markFinished(id);
		if (job.Error || job.Progress >= 0) {
			this.notifyResult(job.Error || "", job.StatusMessageData || []);
			this.emit("done", job.Error || "");
		}
		this.syncJobs();
	};

	private ensurePolling(){
		if (this.pollTimeout) return;
		this.pollTimeout = setTimeout(this.poll, POLL_INTERVAL);
	}

	private poll = async ()=>{
		this.pollTimeout = null;
		if (!this.jobs.length) return;
		try {
			await JobService.fetchJobs();
		} catch (err) {
			console.error(err);
		}
		this.syncJobs();
	};

	private notifySkipped(skipped:ISkippedUploadFile[]){
		let vue = AuthService.mainVueInstance;
		if (!vue || !skipped.length) return;
		let queued = skipped.filter(f=>f.Reason == "queued").map(f=>f.Name);
		let inInbox = skipped.filter(f=>f.Reason == "inbox").map(f=>f.Name);
		if (queued.length) {
			vue.$wf.notify(i18n.t("components.winbox.upload-skipped-queued", {names: queued.join(", ")}), "warning", 6000, "exclamation-triangle");
		}
		if (inInbox.length) {
			vue.$wf.notify(i18n.t("components.winbox.upload-skipped-inbox", {names: inInbox.join(", ")}), "warning", 6000, "exclamation-triangle");
		}
	}

	private notifyResult(error:string, names:string[]){
		let vue = AuthService.mainVueInstance;
		if (!vue) return;
		if (error) {
			vue.$wf.notify(error, "danger", 5000, "exclamation-triangle");
			return;
		}
		let message = names.length == 1
			? i18n.t("components.winbox.document-uploaded", {name: names[0]})
			: i18n.t("components.winbox.documents-uploaded", {count: names.length});
		vue.$wf.notify(message, "success", 4000, "check");
	}
}

export const InboxUpload = Vue.observable(new InboxUploadJob());
